// Calling-hours and Ofcom-compliant time helpers. Reused on both ends so the UI
// can show the same "next allowed window" as the server enforces.

const UK_TIMEZONE = 'Europe/London';

export function toUkParts(d: Date): { day: number; hour: number; minute: number } {
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: UK_TIMEZONE,
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const parts = fmt.formatToParts(d);
  const weekday = parts.find((p) => p.type === 'weekday')?.value ?? 'Mon';
  const hour = parseInt(parts.find((p) => p.type === 'hour')?.value ?? '0', 10);
  const minute = parseInt(parts.find((p) => p.type === 'minute')?.value ?? '0', 10);
  const dayMap: Record<string, number> = {
    Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6,
  };
  return { day: dayMap[weekday] ?? 1, hour, minute };
}

// Calendar date (YYYY-MM-DD) of an instant in Europe/London wall-clock terms.
// Reports bucket by this so a "day" means the same thing whether the server
// clock is UTC or London — reading a local-midnight Date back as a UTC string
// silently shifts every bucket by a day during BST.
export function ukDateKey(d: Date): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: UK_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d);
  const get = (type: string): string => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

// Minutes since midnight, London wall-clock. Used to filter by time-of-day.
export function ukMinutesOfDay(d: Date): number {
  const { hour, minute } = toUkParts(d);
  return hour * 60 + minute;
}

export function isInOfcomCallingHours(now: Date = new Date()): boolean {
  const { day, hour } = toUkParts(now);
  if (day === 0) return false;            // No Sunday marketing calls
  if (day === 6) return hour >= 9 && hour < 17;  // Sat 9-17
  return hour >= 8 && hour < 21;          // Mon-Fri 8-21
}

export function getNextCallingWindowStart(now: Date = new Date()): Date {
  const candidate = new Date(now);
  for (let i = 0; i < 14; i++) {
    const { day } = toUkParts(candidate);
    if (day === 0) {
      // Skip Sunday — try Monday 8am
    } else {
      const startHour = day === 6 ? 9 : 8;
      const startMinute = 0;
      const next = new Date(candidate);
      next.setUTCHours(startHour, startMinute, 0, 0);
      if (next.getTime() > now.getTime() && !isInOfcomCallingHours(now)) {
        return next;
      }
    }
    candidate.setUTCDate(candidate.getUTCDate() + 1);
  }
  return candidate;
}

export function addHours(d: Date, hours: number): Date {
  return new Date(d.getTime() + hours * 60 * 60 * 1000);
}

// Compute the UTC instants representing the start of "today" and "tomorrow"
// in Europe/London wall-clock time. Used by the callback bucket logic so DST
// transitions don't shift the day boundary by an hour.
export function ukDayBoundsUtc(now: Date = new Date()): { startOfTodayUtc: Date; startOfTomorrowUtc: Date } {
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const parts = fmt.formatToParts(now);
  const y = parseInt(parts.find((p) => p.type === 'year')?.value ?? '1970', 10);
  const m = parseInt(parts.find((p) => p.type === 'month')?.value ?? '01', 10);
  const d = parseInt(parts.find((p) => p.type === 'day')?.value ?? '01', 10);
  const startOfTodayUtc = ukWallTimeToUtc(y, m, d, 0, 0);
  const tomorrow = new Date(Date.UTC(y, m - 1, d + 1));
  const tparts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(tomorrow);
  const ty = parseInt(tparts.find((p) => p.type === 'year')?.value ?? '1970', 10);
  const tm = parseInt(tparts.find((p) => p.type === 'month')?.value ?? '01', 10);
  const td = parseInt(tparts.find((p) => p.type === 'day')?.value ?? '01', 10);
  const startOfTomorrowUtc = ukWallTimeToUtc(ty, tm, td, 0, 0);
  return { startOfTodayUtc, startOfTomorrowUtc };
}

// Resolve a London wall-clock target (hour, dayOffset relative to current
// London day) into a UTC Date. dayOffset: 0 = today, 1 = tomorrow, etc.
// If targetWeekday is provided (0=Sun..6=Sat), the function rolls forward to
// the next occurrence of that weekday (still anchored to London time).
export function nextUkWallTime(
  hour: number,
  dayOffset: number,
  now: Date = new Date(),
  targetWeekday?: number,
): Date {
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const parts = fmt.formatToParts(now);
  const y = parseInt(parts.find((p) => p.type === 'year')?.value ?? '1970', 10);
  const m = parseInt(parts.find((p) => p.type === 'month')?.value ?? '01', 10);
  const d = parseInt(parts.find((p) => p.type === 'day')?.value ?? '01', 10);

  let extraDays = dayOffset;
  if (targetWeekday !== undefined) {
    const baseUtc = new Date(Date.UTC(y, m - 1, d + dayOffset));
    const baseDay = toUkParts(baseUtc).day;
    let diff = (targetWeekday - baseDay + 7) % 7;
    if (diff === 0) diff = 7; // strictly the *next* occurrence
    extraDays += diff;
  }

  const target = new Date(Date.UTC(y, m - 1, d + extraDays));
  const tparts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(target);
  const ty = parseInt(tparts.find((p) => p.type === 'year')?.value ?? '1970', 10);
  const tm = parseInt(tparts.find((p) => p.type === 'month')?.value ?? '01', 10);
  const td = parseInt(tparts.find((p) => p.type === 'day')?.value ?? '01', 10);
  return ukWallTimeToUtc(ty, tm, td, hour, 0);
}

// Convert a London wall-clock (y, m, d, hour, minute) tuple to the UTC instant
// it represents. Resolves via Intl rather than guessing offsets so DST is
// respected exactly the same way the rest of this module reads it.
function ukWallTimeToUtc(y: number, m: number, d: number, hour: number, minute: number): Date {
  // Naive UTC guess for that wall-clock time — then probe the actual offset
  // London applies on that date and correct.
  const guess = new Date(Date.UTC(y, m - 1, d, hour, minute));
  const offsetMinutes = londonUtcOffsetMinutes(guess);
  return new Date(guess.getTime() - offsetMinutes * 60 * 1000);
}

function londonUtcOffsetMinutes(d: Date): number {
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const parts = fmt.formatToParts(d);
  const get = (t: string): number => parseInt(parts.find((p) => p.type === t)?.value ?? '0', 10);
  const y = get('year');
  const m = get('month');
  const day = get('day');
  let hour = get('hour');
  const minute = get('minute');
  if (hour === 24) hour = 0; // some ICU builds report 24:00
  const wallAsUtcMs = Date.UTC(y, m - 1, day, hour, minute);
  return Math.round((wallAsUtcMs - d.getTime()) / 60000);
}

export function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds)) return '–';
  const s = Math.max(0, Math.floor(seconds));
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  return `${mm}:${ss.toString().padStart(2, '0')}`;
}
